import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminTutors, updateTutorConfig, getAdminContracts, getContractTemplates, uploadContractTemplate, deleteContractTemplate, assignContract, amendContract, downloadSignedContractAdmin } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Users, Settings, FileText, Sprout, CheckCircle, XCircle,
  AlertTriangle, DollarSign, Loader2, Upload, Trash2, Download,
  MoreHorizontal, ChevronLeft, Shield, PenTool, LogOut, User, Send
} from 'lucide-react';

export default function AdminTutorManagement() {
  const { user, logout } = useAuth();
  const [tutors, setTutors] = useState([]);
  const [contracts, setContracts] = useState({ contracts: [], stats: {} });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ tutor_type: '', hourly_rate: '' });
  const [saving, setSaving] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ file: null, template_type: '', template_name: '' });
  const [uploading, setUploading] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null); // tutor object or null
  const [assignForm, setAssignForm] = useState({ template_file_id: '', contract_title: '', contract_terms: '' });
  const [assigning, setAssigning] = useState(false);
  const [viewDialog, setViewDialog] = useState(null); // contract object for viewing
  const [amendDialog, setAmendDialog] = useState(null); // contract object for amending
  const [amendForm, setAmendForm] = useState({ contract_title: '', contract_terms: '' });
  const [amending, setAmending] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [tutorRes, contractRes, templateRes] = await Promise.all([
        getAdminTutors(), getAdminContracts(), getContractTemplates()
      ]);
      setTutors(tutorRes.data.tutors);
      setContracts(contractRes.data);
      setTemplates(templateRes.data.templates);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openEdit = (tutor) => {
    setEditForm({
      tutor_type: tutor.tutor_type || '',
      hourly_rate: tutor.hourly_rate != null ? String(tutor.hourly_rate) : '',
    });
    setEditDialog(tutor);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const data = {};
      if (editForm.tutor_type) data.tutor_type = editForm.tutor_type;
      if (editForm.hourly_rate) data.hourly_rate = parseFloat(editForm.hourly_rate);
      await updateTutorConfig(editDialog.id, data);
      toast.success('Tutor configuration updated');
      setEditDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadForm.file || !uploadForm.template_type || !uploadForm.template_name) {
      toast.error('All fields are required');
      return;
    }
    setUploading(true);
    try {
      await uploadContractTemplate(uploadForm.file, uploadForm.template_type, uploadForm.template_name);
      toast.success('Contract template uploaded');
      setUploadDialog(false);
      setUploadForm({ file: null, template_type: '', template_name: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (fileId) => {
    try {
      await deleteContractTemplate(fileId);
      toast.success('Template deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const openAssignDialog = (tutor) => {
    setAssignDialog(tutor);
    setAssignForm({ template_file_id: '', contract_title: 'Employment Contract', contract_terms: '' });
  };

  const handleAssignContract = async () => {
    if (!assignForm.contract_terms.trim()) {
      toast.error('Please enter the contract terms');
      return;
    }
    setAssigning(true);
    try {
      await assignContract({
        tutor_id: assignDialog.id,
        template_file_id: assignForm.template_file_id || '',
        contract_title: assignForm.contract_title,
        contract_terms: assignForm.contract_terms,
      });
      toast.success(`Contract assigned to ${assignDialog.name}`);
      setAssignDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign contract');
    } finally {
      setAssigning(false);
    }
  };

  const openAmendDialog = (c) => {
    setAmendDialog(c);
    setAmendForm({ contract_title: c.contract_title || 'Employment Contract', contract_terms: c.contract_terms || '' });
  };

  const handleAmendContract = async () => {
    if (!amendForm.contract_terms.trim()) {
      toast.error('Please enter the contract terms');
      return;
    }
    setAmending(true);
    try {
      await amendContract(amendDialog.user_id, {
        contract_title: amendForm.contract_title,
        contract_terms: amendForm.contract_terms,
      });
      toast.success(`Contract amended and sent to ${amendDialog.name} for re-signing`);
      setAmendDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to amend contract');
    } finally {
      setAmending(false);
    }
  };

  const handleDownloadSignedPdf = async (userId, name) => {
    try {
      await downloadSignedContractAdmin(userId, name);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download signed contract');
    }
  };

  const typeBadge = (type) => {
    if (!type) return <Badge variant="outline" className="text-[#9CA3AF] border-gray-200">Unassigned</Badge>;
    return type === 'main_tutor'
      ? <Badge className="bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20">Main Tutor</Badge>
      : <Badge className="bg-amber-50 text-amber-700 border-amber-200">Standby Tutor</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  const configured = tutors.filter(t => t.tutor_type);
  const compliant = tutors.filter(t => t.fully_compliant);
  const missingRate = tutors.filter(t => t.tutor_type && !t.hourly_rate);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
                <Sprout className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">Tutor Management</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin">
                <Button variant="outline" size="sm" className="text-xs border-gray-200">
                  <ChevronLeft className="w-3 h-3 mr-1" /> Dashboard
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs border-gray-200">
                    <User className="w-3 h-3 mr-1" /> {user?.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Tutors', value: tutors.length, icon: Users, color: 'text-[#111827]' },
            { label: 'Configured', value: configured.length, icon: Settings, color: 'text-[#002FA7]' },
            { label: 'Fully Compliant', value: compliant.length, icon: CheckCircle, color: 'text-[#10B981]' },
            { label: 'Missing Rate', value: missingRate.length, icon: AlertTriangle, color: 'text-[#EF4444]' },
          ].map((s, i) => (
            <Card key={i} className="border border-black/10" data-testid={`tutor-stat-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-[#F9FAFB] rounded-lg flex items-center justify-center border border-gray-100">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <div>
                  <p className="text-xl font-bold text-[#111827]">{s.value}</p>
                  <p className="text-xs text-[#4B5563]">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="tutors">
          <TabsList className="mb-4">
            <TabsTrigger value="tutors" data-testid="tab-tutors">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Pay & Config
            </TabsTrigger>
            <TabsTrigger value="contracts" data-testid="tab-contracts">
              <PenTool className="w-3.5 h-3.5 mr-1.5" /> Contracts
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Templates
            </TabsTrigger>
            <TabsTrigger value="reliability" data-testid="tab-reliability">
              <Shield className="w-3.5 h-3.5 mr-1.5" /> Reliability
            </TabsTrigger>
          </TabsList>

          {/* Tutor Configuration Tab */}
          <TabsContent value="tutors">
            <Card className="border border-black/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Tutor Configuration</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Hourly Rate</TableHead>
                      <TableHead>Onboarding</TableHead>
                      <TableHead>Contract</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tutors.map((t) => (
                      <TableRow key={t.id} data-testid={`tutor-row-${t.id}`}>
                        <TableCell className="font-medium text-sm">{t.name}</TableCell>
                        <TableCell className="text-sm text-[#4B5563]">{t.email}</TableCell>
                        <TableCell>{typeBadge(t.tutor_type)}</TableCell>
                        <TableCell>
                          {t.hourly_rate != null
                            ? <span className="font-semibold text-sm">&pound;{t.hourly_rate.toFixed(2)}/hr</span>
                            : <span className="text-xs text-[#9CA3AF]">Not set</span>
                          }
                        </TableCell>
                        <TableCell>
                          {t.onboarding_status === 'fully_approved'
                            ? <Badge className="bg-green-50 text-green-700 border-green-200">Approved</Badge>
                            : <Badge variant="outline" className="text-amber-600 border-amber-200">{t.onboarding_status.replace(/_/g, ' ')}</Badge>
                          }
                        </TableCell>
                        <TableCell>
                          {t.contract_signed
                            ? <Badge className="bg-green-50 text-green-700 border-green-200">Signed</Badge>
                            : <Badge variant="outline" className="text-[#9CA3AF] border-gray-200">Unsigned</Badge>
                          }
                        </TableCell>
                        <TableCell>
                          {t.fully_compliant
                            ? <CheckCircle className="w-4 h-4 text-[#10B981]" />
                            : <XCircle className="w-4 h-4 text-[#EF4444]" />
                          }
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-tutor-${t.id}`}>
                            <Settings className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {tutors.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-sm text-[#9CA3AF] py-8">No staff members found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Card className="border border-black/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{contracts.stats.total || 0}</p><p className="text-xs text-[#4B5563]">Total</p></CardContent></Card>
              <Card className="border border-black/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#10B981]">{contracts.stats.signed || 0}</p><p className="text-xs text-[#4B5563]">Signed</p></CardContent></Card>
              <Card className="border border-black/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-sky-600">{contracts.stats.assigned || 0}</p><p className="text-xs text-[#4B5563]">Assigned</p></CardContent></Card>
              <Card className="border border-black/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{contracts.stats.pending || 0}</p><p className="text-xs text-[#4B5563]">Pending</p></CardContent></Card>
            </div>
            <Card className="border border-black/10">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Signed At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(contracts.contracts || []).map((c) => (
                      <TableRow key={c.user_id} data-testid={`contract-row-${c.user_id}`}>
                        <TableCell className="font-medium text-sm">{c.name}</TableCell>
                        <TableCell className="text-sm text-[#4B5563]">{c.email}</TableCell>
                        <TableCell>{typeBadge(c.tutor_type)}</TableCell>
                        <TableCell>
                          {c.contract_status === 'signed'
                            ? <Badge className="bg-green-50 text-green-700 border-green-200">Signed</Badge>
                            : c.contract_status === 'assigned'
                              ? <Badge className="bg-sky-50 text-sky-700 border-sky-200">Assigned</Badge>
                              : c.contract_status === 'pending'
                                ? <Badge className="bg-amber-50 text-amber-700 border-amber-200">No Contract</Badge>
                                : <Badge variant="outline" className="text-[#9CA3AF]">No Type Set</Badge>
                          }
                        </TableCell>
                        <TableCell className="text-sm text-[#4B5563]">{c.contract_title || '-'}</TableCell>
                        <TableCell className="text-sm text-[#4B5563]">{c.signed_at ? new Date(c.signed_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {c.contract_status === 'signed' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setViewDialog(c)}
                                  title="View contract"
                                  data-testid={`view-contract-${c.user_id}`}
                                >
                                  <FileText className="w-3.5 h-3.5 text-[#002FA7]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadSignedPdf(c.user_id, c.name)}
                                  title="Download signed PDF"
                                  data-testid={`download-contract-${c.user_id}`}
                                >
                                  <Download className="w-3.5 h-3.5 text-[#4B5563]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openAmendDialog(c)}
                                  title="Amend & Resend"
                                  data-testid={`amend-contract-${c.user_id}`}
                                >
                                  <PenTool className="w-3.5 h-3.5 text-amber-600" />
                                </Button>
                              </>
                            )}
                            {c.contract_status === 'assigned' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setViewDialog(c)}
                                  title="View contract"
                                  data-testid={`view-contract-${c.user_id}`}
                                >
                                  <FileText className="w-3.5 h-3.5 text-[#002FA7]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openAmendDialog(c)}
                                  title="Amend contract"
                                  data-testid={`amend-contract-${c.user_id}`}
                                >
                                  <PenTool className="w-3.5 h-3.5 text-amber-600" />
                                </Button>
                              </>
                            )}
                            {c.contract_status !== 'signed' && c.contract_status !== 'assigned' && c.tutor_type && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
                                onClick={() => openAssignDialog({ id: c.user_id, name: c.name, email: c.email, tutor_type: c.tutor_type })}
                                data-testid={`assign-contract-${c.user_id}`}
                              >
                                <Send className="w-3 h-3 mr-1" /> Assign
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Assign Contract Dialog */}
            {assignDialog && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssignDialog(null)}>
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white" onClick={(e) => e.stopPropagation()} data-testid="assign-contract-dialog">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Assign Contract to {assignDialog.name}</CardTitle>
                    <p className="text-xs text-[#4B5563]">{assignDialog.email} — {assignDialog.tutor_type?.replace('_', ' ')}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-[#374151] block mb-1">Contract Title</label>
                      <Input
                        value={assignForm.contract_title}
                        onChange={(e) => setAssignForm({ ...assignForm, contract_title: e.target.value })}
                        placeholder="e.g. Employment Contract"
                        data-testid="assign-contract-title"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-[#374151] block mb-1">Based on Template (optional)</label>
                      <select
                        className="w-full border border-gray-200 rounded-md p-2 text-sm"
                        value={assignForm.template_file_id}
                        onChange={(e) => setAssignForm({ ...assignForm, template_file_id: e.target.value })}
                        data-testid="assign-contract-template-select"
                      >
                        <option value="">No template — write from scratch</option>
                        {templates.map((t) => (
                          <option key={t.file_id} value={t.file_id}>{t.template_name} ({t.template_type?.replace('_', ' ')})</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-[#374151] block mb-1">Contract Terms <span className="text-red-500">*</span></label>
                      <p className="text-[10px] text-[#9CA3AF] mb-1.5">Write or paste the full contract terms. The staff member will see this text and sign it.</p>
                      <textarea
                        className="w-full border border-gray-200 rounded-md p-3 text-sm min-h-[250px] font-mono leading-relaxed"
                        value={assignForm.contract_terms}
                        onChange={(e) => setAssignForm({ ...assignForm, contract_terms: e.target.value })}
                        placeholder={"EMPLOYMENT CONTRACT\n\nThis agreement is made between Growing Minds Tuition (\"the Company\") and [Staff Name] (\"the Tutor\").\n\n1. ROLE AND RESPONSIBILITIES\n...\n\n2. HOURS AND COMPENSATION\n...\n\n3. TERMINATION\n...\n\n4. CONFIDENTIALITY\n..."}
                        data-testid="assign-contract-terms"
                      />
                    </div>

                    <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
                      <Button variant="outline" size="sm" onClick={() => setAssignDialog(null)} data-testid="assign-contract-cancel">
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAssignContract}
                        disabled={assigning || !assignForm.contract_terms.trim()}
                        className="bg-[#002FA7] hover:bg-[#002482] text-white"
                        data-testid="assign-contract-submit"
                      >
                        {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        Assign & Send
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* View Contract Dialog */}
            {viewDialog && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewDialog(null)}>
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white" onClick={(e) => e.stopPropagation()} data-testid="view-contract-dialog">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{viewDialog.contract_title || 'Contract'} — {viewDialog.name}</CardTitle>
                      {viewDialog.contract_status === 'signed' && (
                        <Button variant="outline" size="sm" className="text-xs" onClick={() => handleDownloadSignedPdf(viewDialog.user_id, viewDialog.name)} data-testid="view-dialog-download">
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {viewDialog.contract_status === 'signed'
                        ? <Badge className="bg-green-50 text-green-700 border-green-200">Signed</Badge>
                        : <Badge className="bg-sky-50 text-sky-700 border-sky-200">Assigned</Badge>}
                      {viewDialog.signature_name && (
                        <span className="text-xs text-[#4B5563]">Signed by: <strong className="italic">{viewDialog.signature_name}</strong> on {new Date(viewDialog.signed_at).toLocaleDateString('en-GB')}</span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {viewDialog.contract_terms ? (
                      <div className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                          <p className="text-xs font-semibold text-[#374151]">CONTRACT TERMS</p>
                        </div>
                        <div className="p-4 max-h-[50vh] overflow-y-auto">
                          <pre className="text-sm text-[#111827] whitespace-pre-wrap font-sans leading-relaxed" data-testid="view-dialog-terms">
                            {viewDialog.contract_terms}
                          </pre>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[#9CA3AF] text-center py-4">No contract terms recorded.</p>
                    )}
                    <div className="flex justify-end mt-4">
                      <Button variant="outline" size="sm" onClick={() => setViewDialog(null)}>Close</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Amend Contract Dialog */}
            {amendDialog && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAmendDialog(null)}>
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white" onClick={(e) => e.stopPropagation()} data-testid="amend-contract-dialog">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Amend & Resend Contract — {amendDialog.name}</CardTitle>
                    <p className="text-xs text-[#4B5563]">Edit the contract terms below. The staff member will need to re-sign after amendments.</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-[#374151] block mb-1">Contract Title</label>
                      <Input
                        value={amendForm.contract_title}
                        onChange={(e) => setAmendForm({ ...amendForm, contract_title: e.target.value })}
                        placeholder="e.g. Employment Contract"
                        data-testid="amend-contract-title"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-[#374151] block mb-1">Contract Terms <span className="text-red-500">*</span></label>
                      <textarea
                        className="w-full border border-gray-200 rounded-md p-3 text-sm min-h-[300px] font-mono leading-relaxed"
                        value={amendForm.contract_terms}
                        onChange={(e) => setAmendForm({ ...amendForm, contract_terms: e.target.value })}
                        placeholder="Enter updated contract terms..."
                        data-testid="amend-contract-terms"
                      />
                    </div>

                    <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
                      <Button variant="outline" size="sm" onClick={() => setAmendDialog(null)} data-testid="amend-contract-cancel">
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAmendContract}
                        disabled={amending || !amendForm.contract_terms.trim()}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="amend-contract-submit"
                      >
                        {amending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PenTool className="w-3.5 h-3.5 mr-1" />}
                        Amend & Resend
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-[#4B5563]">{templates.length} template(s) uploaded</p>
              <Button onClick={() => setUploadDialog(true)} className="bg-[#002FA7] hover:bg-[#002482]" size="sm" data-testid="upload-template-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Template
              </Button>
            </div>
            <div className="grid gap-3">
              {templates.map((t) => (
                <Card key={t.file_id} className="border border-black/10" data-testid={`template-${t.file_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#F9FAFB] rounded-lg flex items-center justify-center border border-gray-100">
                        <FileText className="w-5 h-5 text-[#002FA7]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-[#111827]">{t.template_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {typeBadge(t.template_type)}
                          <span className="text-xs text-[#9CA3AF]">{t.original_filename}</span>
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDeleteTemplate(t.file_id)} className="text-red-600">
                          <Trash2 className="w-3 h-3 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardContent>
                </Card>
              ))}
              {templates.length === 0 && (
                <Card className="border border-dashed border-gray-300">
                  <CardContent className="p-8 text-center">
                    <FileText className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
                    <p className="text-sm text-[#4B5563]">No contract templates uploaded yet.</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Upload separate templates for Main Tutor and Standby Tutor contracts.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Reliability Tab */}
          <TabsContent value="reliability">
            <Card className="border border-black/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Tutor Reliability Scores</CardTitle>
                <p className="text-xs text-[#4B5563] mt-1">Based on session confirmation history. Missed = auto-released (no response by deadline).</p>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Confirmed</TableHead>
                      <TableHead className="text-center">Missed</TableHead>
                      <TableHead className="text-center">Declined</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...tutors].sort((a, b) => (a.reliability_score ?? 100) - (b.reliability_score ?? 100)).map((t) => (
                      <TableRow key={t.id} data-testid={`reliability-row-${t.id}`}>
                        <TableCell className="font-medium text-sm">{t.name}</TableCell>
                        <TableCell>{typeBadge(t.tutor_type)}</TableCell>
                        <TableCell className="text-center text-sm">{t.total_sessions || 0}</TableCell>
                        <TableCell className="text-center text-sm text-emerald-700">{t.confirmed_sessions || 0}</TableCell>
                        <TableCell className="text-center text-sm text-red-600 font-medium">{t.missed_sessions || 0}</TableCell>
                        <TableCell className="text-center text-sm text-amber-600">{t.declined_sessions || 0}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            (t.reliability_score ?? 100) >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            (t.reliability_score ?? 100) >= 70 ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-red-50 text-red-700 border-red-200"
                          }>
                            {t.reliability_score ?? 100}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {tutors.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-[#9CA3AF] py-8">No staff members found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Tutor Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Tutor: {editDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tutor Type</Label>
              <Select value={editForm.tutor_type} onValueChange={(v) => setEditForm(prev => ({ ...prev, tutor_type: v }))}>
                <SelectTrigger data-testid="select-tutor-type"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main_tutor">Main Tutor</SelectItem>
                  <SelectItem value="standby_tutor">Standby Tutor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hourly Rate (&pound;)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={editForm.hourly_rate}
                onChange={(e) => setEditForm(prev => ({ ...prev, hourly_rate: e.target.value }))}
                placeholder="e.g. 25.00"
                data-testid="input-hourly-rate"
              />
              <p className="text-xs text-[#9CA3AF] mt-1">Individual rate for this tutor</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Cancel</Button>
            <Button onClick={saveConfig} disabled={saving} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="save-tutor-config">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Template Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Contract Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Template Name</Label>
              <Input
                value={uploadForm.template_name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, template_name: e.target.value }))}
                placeholder="e.g. Main Tutor Contract 2026"
                data-testid="input-template-name"
              />
            </div>
            <div>
              <Label>Contract Type</Label>
              <Select value={uploadForm.template_type} onValueChange={(v) => setUploadForm(prev => ({ ...prev, template_type: v }))}>
                <SelectTrigger data-testid="select-template-type"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main_tutor">Main Tutor</SelectItem>
                  <SelectItem value="standby_tutor">Standby Tutor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File (PDF or Word)</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                data-testid="input-template-file"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadTemplate} disabled={uploading} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="submit-template-upload">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
